import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { hasLeagueAccess, hasMorpheusAccess, hasNeoAccess, hasOnTimeAccess, normalizeAppName } from "./appAccess";

function getLoginPathForApp(appRequired = "") {
  const normalized = normalizeAppName(appRequired);
  return "/login";
}

function hasRequestedAppAccess(claims = {}, appRequired = "") {
  const required = normalizeAppName(appRequired);
  if (!required) return true;
  if (required === "mc") return hasMorpheusAccess(claims || {});
  if (required === "neo") return hasNeoAccess(claims || {});
  if (required === "ontime") return hasOnTimeAccess(claims || {});
  if (required === "league") return hasLeagueAccess(claims || {});
  return false;
}

function noAccessMessage(appRequired = "") {
  const app = normalizeAppName(appRequired);
  if (app === "mc") return "This account does not have access to Morpheus Count.";
  if (app === "ontime") return "This account does not have access to Link.";
  if (app === "league") return "This account does not have access to Matrix League.";
  return "This account does not have access to Neo.";
}

function FullScreenStatus({ title = "Checking access…", message = "Please wait while MatrixOS verifies your account." }) {
  return (
    <div style={{ minHeight: "100vh", background: "#06101f", color: "white", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ width: "min(560px, 100%)", border: "1px solid rgba(103,232,249,.18)", borderRadius: 24, padding: 28, background: "rgba(8,20,40,.92)", boxShadow: "0 24px 80px rgba(0,0,0,.35)", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ color: "#67e8f9", letterSpacing: ".28em", fontSize: 12, textTransform: "uppercase", marginBottom: 12 }}>Access layer</div>
        <h1 style={{ margin: 0, fontSize: 28 }}>{title}</h1>
        <p style={{ color: "#b7c7dc", lineHeight: 1.6 }}>{message}</p>
      </div>
    </div>
  );
}

export default function RequireAuth({ children, appRequired = "" }) {
  const { user, claims, loading, refreshClaims } = useAuth();
  const location = useLocation();
  const preferredApp = normalizeAppName(appRequired);
  const [checkingAgain, setCheckingAgain] = useState(false);
  const [refreshedOnce, setRefreshedOnce] = useState(false);

  const allowed = useMemo(() => hasRequestedAppAccess(claims || {}, preferredApp), [claims, preferredApp]);

  useEffect(() => {
    setRefreshedOnce(false);
  }, [preferredApp, user?.uid]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      // AuthProvider already resolves token + organization + member access on
      // sign-in. Only retry once when claims are genuinely unavailable; do not
      // force-refresh an already hydrated denied state because that caused the
      // League access screen/login loop.
      if (!user || loading || claims != null || refreshedOnce || typeof refreshClaims !== "function") return;
      setCheckingAgain(true);
      try {
        await refreshClaims({ force: false });
      } finally {
        if (!cancelled) {
          setRefreshedOnce(true);
          setCheckingAgain(false);
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [user, loading, claims, refreshedOnce, refreshClaims]);

  if (loading || checkingAgain) {
    return <FullScreenStatus />;
  }

  if (!user) {
    return <Navigate to={getLoginPathForApp(preferredApp)} state={{ from: location, preferredApp }} replace />;
  }

  if (preferredApp && !allowed) {
    return (
      <Navigate
        to={getLoginPathForApp(preferredApp)}
        state={{
          from: location,
          preferredApp,
          accessDeniedMessage: `❌ ${noAccessMessage(preferredApp)} Please ask an admin to activate it for this organization.`
        }}
        replace
      />
    );
  }

  return children;
}
